import { apiCall } from '@/lib/api-client';
import type { LiveStatusResponse } from '@/types/api';

export type AdminFindhelpStatus = 'uploaded' | 'pending' | 'flagged' | 'no_medicaid';
export type AdminScreeningsStatus = 'submitted' | 'all';
export type AdminScreeningsSort = 'newest' | 'oldest';

export interface AdminScreeningRow {
  id: string;
  submittedAt: string | null;
  agentId: string;
  agentName: string;
  clientLastName: string;
  cinHashSuffix: string;
  durationSeconds: number | null;
  timeCategory: string | null;
  findhelpStatus: AdminFindhelpStatus;
  notesCount: number;
}

export interface AdminScreeningsResponse {
  items: AdminScreeningRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AgentLoadRow {
  id: string;
  fullName: string;
  email: string;
  role: string;
  languages: string[];
  active: boolean;
  currentLoad: number;
}

export const adminApi = {
  /** GET /admin/live — snapshot from live_agent_status materialized view. */
  liveStatus: () => apiCall<LiveStatusResponse>('/admin/live'),

  /**
   * GET /admin/screenings/all — global submitted-screenings list across all
   * agents. Used by Live Monitoring's "All submitted screenings" section.
   */
  listAllScreenings: (opts: {
    status?: AdminScreeningsStatus;
    agentId?: string | null;
    from?: string | null;
    to?: string | null;
    limit?: number;
    offset?: number;
    sort?: AdminScreeningsSort;
  }) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.agentId && opts.agentId !== 'all') p.set('agentId', opts.agentId);
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (typeof opts.limit === 'number') p.set('limit', String(opts.limit));
    if (typeof opts.offset === 'number') p.set('offset', String(opts.offset));
    if (opts.sort) p.set('sort', opts.sort);
    const qs = p.toString();
    return apiCall<AdminScreeningsResponse>(
      `/admin/screenings/all${qs ? '?' + qs : ''}`,
    );
  },

  /** GET /admin/agents — list of agents with current load (used for filter dropdown). */
  listAgents: () => apiCall<AgentLoadRow[]>('/admin/agents'),
};
